import { FC, useEffect } from "react";
import { Fragment, useState } from "react";
import { Listbox, Popover, Switch, Transition } from "@headlessui/react";
import { euro } from "utils/currency";

import { IOffer, ITransportation } from "services/models";
import { useSelector } from "react-redux";
import { Moment } from "moment";
import moment from "moment";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { IOfferCheckOptions } from "./TrasportationDetailPage";
import OffersListBox from "./OffersListBox";

export type OffersByDate = {
  [date: string]: IOfferCheckOptions[];
};

type TransportationTimeSlotsInputProps = {
  offers: OffersByDate;
  handleChange: (date: string, offer: IOffer | null) => void;
  handleFocus?: () => void;
  isDisabled?: boolean;
};

const TransportationPerDateTimeSlotsInput: FC<
  TransportationTimeSlotsInputProps
> = ({ offers, handleChange, handleFocus, isDisabled = false }) => {
  const [selectedOffers, setSelectedOffers] = useState<{
    [date: string]: string | null;
  }>({});
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    const initialSelected: { [date: string]: string | null } = {};
    Object.entries(offers).forEach(([date, offerOptions]) => {
      const selectedOffer = offerOptions.find((offer) => offer.isEnabled);
      initialSelected[date] = selectedOffer
        ? `${selectedOffer.type}-${selectedOffer.price}`
        : null;
    });
    setSelectedOffers(initialSelected);
  }, [offers]);

  const handleCheckboxChange = (
    date: string,
    offerOption: IOfferCheckOptions
  ) => {
    setSelectedOffers((prev) => {
      const uniqueKey = `${offerOption.type}-${offerOption.price}`;
      const isAlreadySelected = prev[date] === uniqueKey;
      const updatedSelected = isAlreadySelected ? null : uniqueKey;

      // Update parent with the new selection
      handleChange(
        date,
        isAlreadySelected
          ? null
          : { type: offerOption.type, price: offerOption.price }
      );
      if (!label || label === "") {
        setLabel(offerOption.type + " " + date);
      } else {
        if (label?.includes(offerOption.type + " " + date)) {
          setLabel(label.replace(offerOption.type + " " + date, ""));
        } else if (label?.includes(date)) {
          let oldOption = label.split(` ${date}`)[0];
          setLabel(label.replace(oldOption, offerOption.type));
        } else {
          if (!label?.includes("...")) {
            setLabel(label + "...");
          }
        }
      }

      return {
        ...prev,
        [date]: updatedSelected,
      };
    });
  };

  useEffect(() => {
    let count = 0;
    Object.entries(selectedOffers).forEach(([date, offerOptions]) => {
      if (offerOptions) {
        count++;
      }
    });
    if (count === 1 && label?.includes("...")) {
      setLabel(label.replace("...", ""));
    }
  }, [selectedOffers]);

  const handleClick = () => {
    document.querySelector("html")?.click();
    handleFocus && handleFocus();
  };
  return (
    <div className="flex flex-1">
      <Popover className="flex relative h-full">
        {({ open }) => (
          <>
            <div
              className={`flex-1 flex items-center focus:outline-none cursor-pointer ${
                open ? "nc-hero-field-focused" : ""
              }`}
            >
              <Popover.Button
                className="flex-1 flex text-left items-center p-4 w-[179px] space-x-3"
                onClick={handleClick}
              >
                <div className="text-neutral-300 dark:text-neutral-400">
                  <div className="text-4xl">
                    <i className="las la-car-alt"></i>
                  </div>
                </div>
                <div className="flex-grow">
                  <span className="block xl:text-lg font-semibold">
                    {!label ? "Service" : label}
                  </span>
                  <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
                    Options
                  </span>
                </div>
              </Popover.Button>
            </div>
            {!isDisabled && (
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className="absolute right-0 z-10 w-[350px] max-w-sm bg-white dark:bg-neutral-800 
                      top-full mt-3 py-5 sm:py-6 px-4 sm:px-8 rounded-3xl shadow-xl h-112 overflow-y-scroll disable-scroll"
                >
                  <div className="flex flex-col justify-center px-5 py-6 space-y-4">
                    {Object.entries(offers).map(([date, offerOptions]) => (
                      <div key={date} className="flex flex-col space-y-2">
                        <div className="text-lg font-medium">
                          {moment(date).format("DD/MM/YYYY")}
                        </div>
                        <div className="space-y-1">
                          {offerOptions.map(
                            (
                              offerOption: IOfferCheckOptions,
                              index: number
                            ) => {
                              const uniqueKey = `${offerOption.type}-${offerOption.price}-${index}`;
                              const isChecked =
                                selectedOffers[date] ===
                                `${offerOption.type}-${offerOption.price}`;

                              return (
                                <label
                                  key={uniqueKey}
                                  className="flex items-center space-x-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() =>
                                      handleCheckboxChange(date, offerOption)
                                    }
                                    className="focus:ring-action-primary h-6 w-6 text-primary-500 border-primary rounded border-neutral-500 bg-white dark:bg-neutral-700  dark:checked:bg-primary-500 focus:ring-primary-500"
                                  />
                                  <span className="flex-1 text-sm text-neutral-700 dark:text-neutral-300">
                                    {offerOption.type}{" "}
                                    {offerOption.price
                                      ? `- ${euro.format(offerOption.price)}`
                                      : ""}
                                  </span>
                                  {/* Removed the blue CheckIcon */}
                                </label>
                              );
                            }
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            )}
          </>
        )}
      </Popover>
    </div>
  );
};

export default TransportationPerDateTimeSlotsInput;
